import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PlayerProfileForm from "../../components/profile/PlayerProfileForm";
import { userService } from "../../services/api";

const PlayerProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL or default to my-profile for players
  const activeTab = searchParams.get("tab") || "my-profile";

  // Player Profile API state
  const [playerProfileData, setPlayerProfileData] = useState(null);
  const [formApiData, setFormApiData] = useState({
    first_name: "",
    last_name: "",
    country: "",
    height: "",
    weight: "",
    foot: "",
    date_of_birth: "",
    player_image: "",
    position: "",
    player_id: "",
    email: "",
    phone: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return `${age}yrs`;
  };

  // Map API data to form fields
  const mapApiToForm = (data) => ({
    first_name: data.first_name || data.name?.split(' ')[0] || "",
    last_name: data.last_name || (data.name?.split(' ').slice(1).join(' ')) || "",
    // Country can be an object {name, image} or a string
    country: typeof data.country === 'object' ? (data.country?.name || "") : (data.country || ""),
    height: data.height ? String(data.height) : "",
    weight: data.weight ? String(data.weight) : "",
    foot: data.foot || "",
    date_of_birth: data.date_of_birth || "",
    // Use player_image first, fallback to player_profile_picture, then profile_picture_url
    player_image: data.player_image || data.player_profile_picture || data.profile_picture_url || "",
    position: data.position || "",
    player_id: data.wyscout_id || "",
    email: data.email || "",
    phone: data.phone_number || data.mobile || "",
  });

  // Map form fields to API payload
  const mapFormToApi = (form) => {
    // Create FormData for multipart/form-data submission
    const formData = new FormData();
    
    // Debug: Log what we're sending
    console.log("mapFormToApi - form data:", form);
    console.log("mapFormToApi - player_image:", form.player_image, "type:", typeof form.player_image, "instanceof File:", form.player_image instanceof File);
    
    // Append text fields
    if (form.first_name) formData.append("first_name", form.first_name);
    if (form.last_name) formData.append("last_name", form.last_name);
    // Ensure country is a string, not an object
    if (form.country) {
      const countryValue = typeof form.country === 'object' ? (form.country?.name || "") : form.country;
      if (countryValue) formData.append("country", countryValue);
    }
    if (form.height) formData.append("height", form.height);
    if (form.weight) formData.append("weight", form.weight);
    if (form.foot) formData.append("foot", form.foot);
    if (form.date_of_birth) formData.append("date_of_birth", form.date_of_birth);
    if (form.position) formData.append("position", form.position);
    if (form.email) formData.append("email", form.email);
    if (form.phone) formData.append("phone_number", form.phone);
    
    // Only append profile picture if it's a File object (new upload)
    // Update both profile_picture and player_image fields
    if (form.player_image instanceof File) {
      formData.append("profile_picture", form.player_image);
      formData.append("player_image", form.player_image);
    }
    
    return formData;
  };

  // Fetch player profile on mount
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError("");
    
    userService.getPlayerProfile()
      .then((result) => {
        if (isMounted) {
          if (result.success) {
            setPlayerProfileData(result.data);
            console.log("profile", result.data);
            setFormApiData(mapApiToForm(result.data));
          } else {
            setError(result.error);
          }
        }
      })
      .catch((err) => {
        console.log(err);
        if (isMounted) {
          setError("Failed to load profile");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Handle API-editable field changes
  const handleApiInputChange = (field, value) => {
    setFormApiData((prev) => ({ ...prev, [field]: value }));
  };

  const handleMobileUpdate = (newMobile) => {
    setFormApiData((prev) => ({ ...prev, phone: newMobile }));
    setPlayerProfileData((prev) =>
      prev
        ? {
            ...prev,
            phone: newMobile,
            phone_number: newMobile,
            mobile: newMobile,
          }
        : prev
    );
  };

  // Handle file upload for profile image
  const handleProfileImageChange = (file) => {
    console.log("handleProfileImageChange called with:", file, "instanceof File:", file instanceof File);
    setFormApiData((prev) => ({ ...prev, player_image: file }));
  };

  // Clear image upload loading state after save completes
  useEffect(() => {
    if (!isSaving && formApiData.player_image instanceof File) {
      // Image was uploaded and save completed, clear any loading states
      // The loading state is managed in PlayerProfileForm component
    }
  }, [isSaving, formApiData.player_image]);

  // Save changes to API
  const handleSave = async () => {
    setIsSaving(true);
    setError("");
    
    try {
      const payload = mapFormToApi(formApiData);
      const result = await userService.updatePlayerProfile(payload);
      
      if (result.success) {
        // Clear any File object from formApiData before updating with API response
        // This ensures the blob URL is cleared and API URL is used
        setPlayerProfileData(result.data);
        const updatedFormData = mapApiToForm(result.data);
        // Ensure player_image uses the API URL (prioritize player_image, then player_profile_picture)
        updatedFormData.player_image = result.data.player_image || result.data.player_profile_picture || result.data.profile_picture_url || "";
        setFormApiData(updatedFormData);
        setIsEditing(false);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing and revert changes
  const handleCancel = () => {
    if (playerProfileData) {
      setFormApiData(mapApiToForm(playerProfileData));
    }
    setIsEditing(false);
    setError("");
  };

  // Update URL when tab changes
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const tabs = [
    { id: "my-profile", label: "My Profile" },
  ];

  const renderTabContent = () => {
    return (
      <PlayerProfileForm
        playerProfileData={playerProfileData}
        formApiData={formApiData}
        isEditing={isEditing}
        isLoading={isLoading}
        isSaving={isSaving}
        error={error}
        onApiInputChange={handleApiInputChange}
        onProfileImageChange={handleProfileImageChange}
        onSave={handleSave}
        onCancel={handleCancel}
        onEdit={() => setIsEditing(true)}
        calculateAge={calculateAge}
            onMobileUpdate={handleMobileUpdate}
      />
    );
  };

  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-auto">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white py-4 border-b border-[#E5E6E8]">
          <div className="sm:px-14 px-4 flex items-center justify-between">
            <h1 className="text-[20px] font-bold text-gray-900">My Profile</h1>
            <button onClick={() => navigate(`/search/player/${formApiData.player_id}`)} className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg">
              Preview public profile
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white py-4 flex items-center">
          <div className="sm:px-14 px-4">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-4 font-medium text-sm rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? "bg-[#F3F4F6] text-[#0B0B0B]"
                      : "text-[#1E1E1ECC] hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-[#F8F9FB] rounded-lg shadow-sm">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default PlayerProfile;
